

// Copyright (c) Docugami, Inc. All rights reserved.
import React from 'react';
import { greyText, homeGray, emptyGray, primaryTheme } from '../../shell/presentation/AppStyles';
import { DgIconButton } from 'components/shell/utils/DgIconButton';
import { dgIconNames, useDgIcons } from 'hooks/dgIcons';
import { SmallButtonLabel, dgFontFamily } from 'components/common/Fonts';
import styled, { css } from 'styled-components';

const CountText = styled(SmallButtonLabel)`
    color: ${greyText};
    font-size: 12px;
`;

const CountTextContainer = styled.div`
    min-width: 30px;
    margin: 5px;
    display: flex;
    justify-content: flex-end;
`;

const Divider = styled.div`
    border-right: 1px solid ${emptyGray};
    height: 20px;
    width: 5px;
    margin: 5px;
`;

const SearchInput = styled.input`
    font-size: 12px;
    font-weight: 500;
    width: 100px;
    background: none;
    border: none;
    padding: 5px;
    outline: none;
    font-family: ${dgFontFamily};
    border-radius: 5px;
`;

const ActionButtonCss = css`
    cursor: pointer;
    :hover {
        opacity: 1;
        transform: scale(1.08);
    };
    :active:hover {
        transform: scale(1);
    };
`;

const SearchNavButton = styled.div<{ disabled: boolean }>`
    display: flex;
    margin-right: 5px;
    opacity: 0.6;
    ${props => !props.disabled ? ActionButtonCss : ''}
`;

const SearchButtons = styled.div`
    padding: 3px;
    display: flex;
    flex-direction: row;
    align-items: center;
    margin-bottom: 5px;
    width: 55px;
    justify-content: space-between;
`;

const SearchBoxContainer = styled.div<{ searchOpen: boolean }>`
    display: flex;
    flex-direction: row;
    background: ${primaryTheme.palette.white};
    border: 1px solid ${emptyGray};
    border-radius: 5px;
    align-items: center;
    width: ${props => props.searchOpen ? "230px" : "0px"};
    overflow: hidden;
    transition: width 0.2s linear 0s;
    ${props => !props.searchOpen && 'border: none'};
`;

const SearchIcon = styled.div<{ searchOpen: boolean }>`
    display: ${props => props.searchOpen ? "none" : "flex"};
    visibility: ${props => props.searchOpen ? "hidden" : "visible"};
    transition-property: visibility;
    transition-duration: 0.2s;
    transition-timing-function: linear;
    transition-delay: 0.2s;
`;

const SearchContainer = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    align-items: center;
`;

export interface SearchBarProps {
    inputRef: React.RefObject<HTMLInputElement>;
    searchIsOpen: boolean;
    currentResultIndex: number;
    numberOfResults?: number;
    currentValue: string;
    onSearchIconClick: () => void;
    onChange: (ev: any) => void;
    handleKeyPress: (ev: any) => void;
    onNavigatePrev: () => void;
    onNavigateNext: () => void;
    onClose: () => void;
}

export const SearchBar = (props: SearchBarProps): JSX.Element => {
    const dgIcons = useDgIcons();

    function getCountDisplay(): JSX.Element {
        let count: string = '';
        if (props.numberOfResults !== undefined) {
            count = `${Math.min(props.numberOfResults, props.currentResultIndex + 1)}/${props.numberOfResults}`
        }

        return (
            <CountTextContainer>
                <CountText>{count}</CountText>
            </CountTextContainer>
        );
    }

    function getSearchIcon(): JSX.Element {
        return <SearchIcon searchOpen={props.searchIsOpen}>
            <DgIconButton iconName={dgIconNames.DgSearchIcon} width={20} height={20} color={homeGray} shadow={''} onClick={props.onSearchIconClick} />
        </SearchIcon>
    }

    function getSearchButtons() {
        const downChevron = dgIcons.icon(dgIconNames.DgSearchDownChevron, '', 15, 12, homeGray, '');
        const upChevron = dgIcons.icon(dgIconNames.DgSearchUpChevron, '', 15, 12, homeGray, '');
        const closeIcon = dgIcons.icon(dgIconNames.DgCloseIcon, '', 10, 10, homeGray, '');

        const navigationDisabled = !props.numberOfResults || props.numberOfResults < 1;

        return <SearchButtons>
            <SearchNavButton disabled={navigationDisabled} onClick={props.onNavigateNext}>{downChevron}</SearchNavButton>
            <SearchNavButton disabled={navigationDisabled} onClick={props.onNavigatePrev}>{upChevron}</SearchNavButton>
            <SearchNavButton disabled={false} onClick={props.onClose}>{closeIcon}</SearchNavButton>
        </SearchButtons>
    }

    return (
        <SearchContainer>
            <SearchBoxContainer searchOpen={props.searchIsOpen}>
                <SearchInput ref={props.inputRef} id="search_input" type="text" placeholder="Search" value={props.currentValue} onChange={props.onChange} onKeyPress={props.handleKeyPress} />
                {getCountDisplay()}
                <Divider />
                {getSearchButtons()}
            </SearchBoxContainer>
            {getSearchIcon()}
        </SearchContainer>
    );
}